import React, { useEffect, useState } from "react";
import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import TextInput from "presentation/components/UI/TextInput";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { Button } from "presentation/components/UI/Button";
import { mailIntegrationUseCase } from "../../../di/container";
import type {
  MailServiceProvider,
  SaveMailIntegrationRequest,
} from "../../../domain/types/mail-integration";
import { useNotifications } from "../../hooks/use-notifications";
import NotificationsList from "../../components/UI/NotificationList";
import { MailSenderSettings } from "../../components/CompanyAdmin/EmailSettings/MailSenderSettings";
import {
  AppPasswordGuideDialog,
  GuideButton,
  MailAccountDialog,
  ProviderNotice,
  ProviderPicker,
} from "../../components/CompanyAdmin/EmailSettings/mailAccountUi";
import { Plus } from "lucide-react";

interface MailSyncSetting {
  provider: "NAVER" | "KAKAO" | "GMAIL" | "OTHERS";
  emailAddress: string;
  password: string;
  customImapHost?: string;
  customImapPort?: number;
}

const EmailSettings: React.FC = () => {
  const { companyId } = useParams();

  const {
    notifications,
    addItem: addNotificationItem,
    removeItem,
  } = useNotifications();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    setFocus,
    formState: { isDirty, errors },
  } = useForm<MailSyncSetting>({
    defaultValues: {
      provider: "NAVER",
      emailAddress: "",
      password: "",
    },
  });

  // 초기 provider 값을 저장하여 변경 여부 추적
  const [initialProvider, setInitialProvider] = useState<MailSyncSetting["provider"]>("NAVER");

  // 로컬 에러 상태 (입력 시 클리어용)
  const [localErrors, setLocalErrors] = useState<{
    emailAddress?: string;
    password?: string;
  }>({});

  // 폼의 provider 값을 백엔드 MailServiceProvider 로 변환
  const providerMap: Record<MailSyncSetting["provider"], MailServiceProvider> = {
    NAVER: "NAVER",
    KAKAO: "DAUM",
    GMAIL: "GOOGLE",
    OTHERS: "OTHER",
  };

  // 백엔드 MailServiceProvider 를 폼의 provider 값으로 변환
  const toFormProvider = (
    provider: MailServiceProvider,
  ): MailSyncSetting["provider"] => {
    switch (provider) {
      case "NAVER":
        return "NAVER";
      case "GOOGLE":
        return "GMAIL";
      case "DAUM":
        return "KAKAO";
      default:
        // OTHER
        return "OTHERS";
    }
  };

  const [selectedProvider, setSelectedProvider] = useState<
    "NAVER" | "KAKAO" | "GMAIL" | "OTHERS"
  >("NAVER");

  const [showGuideDialog, setShowGuideDialog] = useState(false);
  // 기본 계정(주문 메일함) 추가·수정 다이얼로그
  const [showDefaultDialog, setShowDefaultDialog] = useState(false);
  // 보내기 전용 계정 추가 다이얼로그
  const [showSenderDialog, setShowSenderDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // 폼이 수정되었는지 확인 (provider 변경도 포함)
  const isFormModified = isDirty || selectedProvider !== initialProvider;

  useEffect(() => {
    setValue("provider", selectedProvider);
  }, [setValue, selectedProvider]);

  const [isPasswordAlreadySet, setIsPasswordAlreadySet] = useState(false);
  // 저장된 주문 메일함 계정 주소. 기능별 발신 계정의 '기본 계정' 으로 보여 준다
  const [savedDefaultEmail, setSavedDefaultEmail] = useState<string | null>(null);

  // 페이지 로드 시 기존 메일 연동 설정을 불러와 폼을 초기화
  useEffect(() => {
    const cid = Number(companyId);
    if (!Number.isFinite(cid)) return;

    const fetchSetting = async () => {
      try {
        setLoading(true);
        const setting = await mailIntegrationUseCase.getSetting(cid);
        if (!setting) return;
        setSavedDefaultEmail(setting.senderEmail ?? null);

        // 연동 미설정 시 provider 는 null 로 내려오므로 기본값으로 대체
        const formProvider = setting.provider
          ? toFormProvider(setting.provider)
          : "NAVER";
        setSelectedProvider(formProvider);
        setInitialProvider(formProvider); // 초기 provider 저장
        reset({
          provider: formProvider,
          emailAddress: setting.senderEmail ?? "",
          password: "",
          customImapHost: setting.customImapHost ?? undefined,
          customImapPort: setting.customImapPort ?? undefined,
        });

        if (setting.hasPassword) {
          setIsPasswordAlreadySet(true);
        }
      } catch (error) {
        console.error("Error fetching mail integration setting:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSetting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const handleSave = async (data: MailSyncSetting) => {
    const cid = Number(companyId);
    if (!Number.isFinite(cid)) return;

    const request: SaveMailIntegrationRequest = {
      provider: providerMap[data.provider],
      senderEmail: data.emailAddress,
      // 비밀번호는 새로 입력한 경우에만 전달 (비어있으면 기존 값 유지)
      ...(data.password ? { senderEmailPassword: data.password } : {}),
      // 커스텀 IMAP 호스트/포트는 메일 서비스가 "기타"일 때만 전송
      ...(data.provider === "OTHERS" && {
        customImapHost: data.customImapHost,
        customImapPort:
          data.customImapPort != null ? Number(data.customImapPort) : undefined,
      }),
    };

    try {
      setSaving(true);
      await mailIntegrationUseCase.saveSetting(cid, request);
      setIsPasswordAlreadySet(true);
      setSavedDefaultEmail(data.emailAddress);
      // 저장 성공 시 초기값 업데이트 (폼을 "수정 전" 상태로 전환)
      setInitialProvider(selectedProvider);
      reset({
        provider: selectedProvider,
        emailAddress: data.emailAddress,
        password: "",
        customImapHost: data.customImapHost,
        customImapPort: data.customImapPort,
      });
      setShowDefaultDialog(false);
      addNotificationItem("메일 연동 설정이 저장되었습니다.");
    } catch (error) {
      console.error("Error saving mail integration setting:", error);
      addNotificationItem(
        "메일 연동 설정에 실패하였습니다. 기입된 정보가 맞는지 확인해 주세요.",
      );
    } finally {
      setSaving(false);
    }
  };

  // 폼 제출 전 필수값 검증 및 포커스
  const onSubmit = (data: MailSyncSetting) => {
    setLocalErrors({}); // 제출 성공 시 에러 클리어
    handleSave(data);
  };

  const onError = () => {
    // 에러를 로컬 상태에 저장
    setLocalErrors({
      emailAddress: errors.emailAddress?.message,
      password: errors.password?.message,
    });
    // 첫 번째 에러 필드에 포커스
    if (errors.emailAddress) {
      setFocus("emailAddress");
    } else if (errors.password) {
      setFocus("password");
    }
  };

  // 입력 시 해당 필드의 에러 클리어
  const clearFieldError = (field: "emailAddress" | "password") => {
    if (localErrors[field]) {
      setLocalErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  // 다이얼로그를 닫으면 입력하던 값은 버리고 저장된 값으로 되돌린다
  const closeDefaultDialog = () => {
    setSelectedProvider(initialProvider);
    setLocalErrors({});
    reset();
    setShowDefaultDialog(false);
  };

  // 우측 상단 '추가': 기본 계정이 없으면 기본 계정(주문 메일함)부터, 있으면 보내기 전용 계정을 추가한다
  const handleAdd = () => {
    if (savedDefaultEmail) setShowSenderDialog(true);
    else setShowDefaultDialog(true);
  };

  if (loading) {
    return (
      <CompanyAdminLayout>
        <div className="flex justify-center items-center h-64">
          <div className="text-[#888]">로딩 중...</div>
        </div>
      </CompanyAdminLayout>
    );
  }

  return (
    <>
      <NotificationsList notifications={notifications} />
      <CompanyAdminLayout>
        <div className="p-6 flex justify-center">
          <div className="w-full max-w-2xl text-left">
            <div className="flex items-center justify-between mb-3">
              <h1 className="text-[22px] font-bold text-[#222]">이메일 설정</h1>
              <button
                type="button"
                onClick={handleAdd}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-[8px] bg-[#222] text-white text-[13px] font-medium hover:bg-[#333]"
              >
                <Plus className="w-4 h-4" /> 추가
              </button>
            </div>

            {Number.isFinite(Number(companyId)) && (
              <MailSenderSettings
                companyId={Number(companyId)}
                defaultEmail={savedDefaultEmail}
                onEditDefault={() => setShowDefaultDialog(true)}
                showAddDialog={showSenderDialog}
                onCloseAddDialog={() => setShowSenderDialog(false)}
              />
            )}

            {/* 앱 비밀번호 발급 방법 (계정 다이얼로그 위에 뜬다) */}
            {showGuideDialog && selectedProvider !== "OTHERS" && (
              <AppPasswordGuideDialog provider={selectedProvider} onClose={() => setShowGuideDialog(false)} />
            )}

            {/* 기본 계정(주문 메일함) 추가·수정. 주문 메일 수신과, 기능별로 따로 고르지 않은 메일의 발신에 쓴다 */}
            {showDefaultDialog && (
              <MailAccountDialog title={savedDefaultEmail ? "기본 계정 수정" : "기본 계정 추가"} onClose={closeDefaultDialog}>
                <p className="text-[13px] text-[#888] mb-4">
                  주문 메일함으로 주문 메일을 받아 오고, 기능별로 따로 고르지 않은 메일을 이 계정으로 보냅니다.
                </p>
            <form onSubmit={handleSubmit(onSubmit, onError)} className="text-left">
              <div className="space-y-4">
                {/* 메일 서비스 선택 - 토스페이 스타일 */}
                <div>
                  <label className="block text-[14px] font-medium text-[#333] mb-3 text-left">
                    메일 서비스 <span className="text-[#F24A4A]">*</span>
                  </label>
                  <ProviderPicker value={selectedProvider} onChange={setSelectedProvider} />
                  <ProviderNotice provider={selectedProvider} />
                </div>

                {/* 발신 이메일 */}
                <div>
                  <label className="block text-[14px] font-medium text-[#333] mb-2 text-left">
                    발신 이메일 주소 <span className="text-[#F24A4A]">*</span>
                  </label>
                  <TextInput
                    {...register("emailAddress", {
                      required: {
                        value: true,
                        message: "이메일 주소를 입력해주세요.",
                      },
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "올바른 이메일 형식을 입력해주세요.",
                      },
                    })}
                    onInput={() => clearFieldError("emailAddress")}
                    placeholder="example@example.com"
                    error={
                      localErrors.emailAddress
                        ? { message: localErrors.emailAddress, isMessageVisible: true, isIconVisible: true }
                        : undefined
                    }
                  />
                  {!localErrors.emailAddress && (
                    <p className="text-[12px] text-[#888] mt-1 text-left">
                      고객에게 발송되는 이메일의 발신자 주소입니다.
                    </p>
                  )}
                </div>

                {/* 앱 비밀번호 */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-[14px] font-medium text-[#333] text-left">
                      앱 비밀번호 <span className="text-[#F24A4A]">*</span>
                      {isPasswordAlreadySet && (
                        <span className="ml-2 text-[12px] text-[#059669] font-normal">
                          (설정됨)
                        </span>
                      )}
                    </label>
                    {/* 발급 방법 안내 버튼 - 기타가 아닌 경우에만 표시 */}
                    {selectedProvider !== "OTHERS" && (
                      <GuideButton onClick={() => setShowGuideDialog(true)} />
                    )}
                  </div>
                  <TextInput
                    type="password"
                    placeholder={
                      isPasswordAlreadySet
                        ? "변경하려면 새 비밀번호를 입력하세요"
                        : "앱 비밀번호를 입력하세요"
                    }
                    {...register("password", {
                      required: {
                        value: !isPasswordAlreadySet,
                        message: "앱 비밀번호를 입력해주세요.",
                      },
                      // 앱 비밀번호는 "abcd efgh ijkl mnop" 처럼 공백 포함으로 복사되는 경우가 많아
                      // 입력/붙여넣기 시 공백을 자동 제거한다 (폼 상태와 화면 입력값 모두 갱신).
                      onChange: (e) => {
                        const sanitized = e.target.value.replace(/\s/g, "");
                        if (sanitized !== e.target.value) {
                          setValue("password", sanitized, { shouldDirty: true });
                        }
                      },
                    })}
                    onInput={() => clearFieldError("password")}
                    error={
                      localErrors.password
                        ? { message: localErrors.password, isMessageVisible: true, isIconVisible: true }
                        : undefined
                    }
                  />
                  {!localErrors.password && (
                    <p className="text-[12px] text-[#888] mt-1 text-left">
                      계정 비밀번호가 아닌, 앱 비밀번호(애플리케이션 비밀번호)를 입력하세요.
                    </p>
                  )}
                </div>

                {/* 기타 선택 시 커스텀 IMAP 설정 */}
                {selectedProvider === "OTHERS" && (
                  <>
                    <div>
                      <label className="block text-[14px] font-medium text-[#333] mb-2 text-left">
                        커스텀 IMAP 호스트
                      </label>
                      <TextInput
                        type="text"
                        placeholder="imap.example.com"
                        {...register("customImapHost")}
                      />
                    </div>
                    <div>
                      <label className="block text-[14px] font-medium text-[#333] mb-2 text-left">
                        커스텀 IMAP 포트
                      </label>
                      <TextInput
                        type="text"
                        placeholder="993"
                        {...register("customImapPort")}
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={closeDefaultDialog}>
                  취소
                </Button>
                <Button type="submit" variant="primary" disabled={!isFormModified || saving}>
                  {saving ? "연결 확인 중..." : "저장"}
                </Button>
              </div>
            </form>
              </MailAccountDialog>
            )}
          </div>
        </div>
      </CompanyAdminLayout>
    </>
  );
};

export default EmailSettings;
